import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Globe2, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { sel } from "../components/configuratorMetrics";
import { COMP_METRICS } from "../components/comparatiiMetrics";
import { DistributieCard, TabelTariCard } from "../components/ComparatiiCards";
import ComparatiiOverview from "../components/ComparatiiOverview";

const dayIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function Comparatii() {
  const [f, setF] = useState({ date_from: dayIso(29), date_to: dayIso(0) });
  const [metric, setMetric] = useState("oferte_config");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!f.date_from || !f.date_to) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/api/configurator/comparativ-tari", { params: f });
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Eroare la incarcare");
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const { data: blob } = await api.get("/api/configurator/comparativ-tari/export", { params: f, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `comparativ_tari_${f.date_from}_${f.date_to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Export esuat");
    } finally {
      setExporting(false);
    }
  };

  const series = data?.series?.[metric];
  const total = series?.total || 0;
  const pieData = useMemo(() => Object.entries(series?.by_country || {})
    .filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value })), [series]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const label = COMP_METRICS.find(([k]) => k === metric)?.[1];

  return (
    <Layout title="Comparatii">
      <div data-testid="comparatii-page">
        <div className="mb-5">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800"><Globe2 size={20} className="text-cyan-600" /> Rapoarte comparative pe tari</h2>
          <p className="mt-1 text-sm text-slate-500">Distributie pe tari (GeoIP sesiuni) pentru cele 19 metrici BI.</p>
        </div>

        <div className="mb-5 flex flex-wrap items-center gap-3 rounded border border-slate-200 bg-white p-4 text-sm shadow-sm">
          <label className="flex items-center gap-2">De la
            <input data-testid="comparatii-date-from" type="date" className={sel} value={f.date_from} max={f.date_to} onChange={set("date_from")} />
          </label>
          <label className="flex items-center gap-2">Pana la
            <input data-testid="comparatii-date-to" type="date" className={sel} value={f.date_to} min={f.date_from} max={dayIso(0)} onChange={set("date_to")} />
          </label>
          <label className="flex items-center gap-2">Metrica
            <select data-testid="comparatii-metric" className={sel} value={metric} onChange={(e) => setMetric(e.target.value)}>
              {COMP_METRICS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          {data?.total_zile ? <span data-testid="comparatii-total-zile" className="text-xs text-slate-500">{data.total_zile} zile</span> : null}
          <button data-testid="comparatii-export-btn" onClick={exportXlsx} disabled={exporting || loading || !data} className="ml-auto inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50">
            <Download size={14} /> {exporting ? "Export..." : "Export Excel"}
          </button>
        </div>

        {loading && <div data-testid="comparatii-loading" className="mb-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Se incarca...</div>}
        {error && <div data-testid="comparatii-error" className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <DistributieCard label={label} pieData={pieData} total={total} />
          <TabelTariCard pieData={pieData} total={total} />
        </div>
        <ComparatiiOverview data={data} metric={metric} />
      </div>
    </Layout>
  );
}
